import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import Group from 'components/utils/Group'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import TextButton from 'LuxKit/Button/TextButton'
import { canCancelOrderItem, canExtendHotelDates, canRequestChangeHotelDates, getFreeCancellationPeriodEndDate } from 'supportAssistant/utils/orderUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ImageWithReservedSpace from 'components/Common/ImageWithReservedSpace'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { buildChatMessageMetaPayload } from 'supportAssistant/utils/messageUtils'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import { ChatActions } from '@luxuryescapes/contract-support'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import { extractOccupantsFromReservation } from 'lib/checkout/cartReservationUtils'
import { canAddExtraGuests } from 'lib/order/extraGuestUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const ImageWrapper = styled.div`
  width: 100%;
  ${mediaQueryUp.tablet} {
    width: ${rem(100)};
  }
`

interface Props {
  item: App.OrderItem
  order: App.Order
  offer: App.Offer
  roomLabel: string
  hideSelectButton?: boolean
  isCurrentOrder?: boolean
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
}

function LesaHotelItemSummary(props: Props) {
  const { item, order, offer, roomLabel, hideSelectButton = false, isCurrentOrder, componentsMap } = props
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const [cancellationConfig] = useOrderCancellationConfigs(item.id, order.id)

  const dispatch = useAppDispatch()

  const bookingNumber = item.bookingNumber
  const { rates } = useOfferAvailableRates(item?.offerId, {
    checkIn: item.reservation?.startDate,
    checkOut: item.reservation?.endDate,
    rooms: extractOccupantsFromReservation(item.reservation),
  })

  const canAddGuests = canAddExtraGuests(offer as App.HotelOffer, order, item, rates, { canRefund: cancellationConfig?.canRefund })

  const canChangeDates = !!cancellationConfig.canChangeDates
  const canRequestChangeDates = canRequestChangeHotelDates(order, offer, cancellationConfig)
  const canExtendDates = canChangeDates && canExtendHotelDates(order, offer, item.id, cancellationConfig)
  const canBeCancelled = canCancelOrderItem(order, item, cancellationConfig)
  const freeCancellationPeriodEndDate = getFreeCancellationPeriodEndDate(order, item, cancellationConfig) ?? ''

  const action: ChatActions = componentsMap?.get('list-order-items-extra-guests') ? ChatActions.SELECT_ORDER_ITEM_EXTRA_GUESTS : ChatActions.SELECT_ORDER_ITEM

  const onSelect = useCallback(() => {
    dispatch(addChatMessage(item.bookingNumber, {
      action,
      payload: {
        ...buildChatMessageMetaPayload(order, item, offer, {
          isCurrentOrder,
          canRequestChangeDates,
          canExtendDates,
          checkInDate: item.reservation?.startDate,
          canChangeDates,
          canBeCancelled,
          freeCancellationPeriodEndDate,
          orderType: 'hotel',
          canAddExtraGuests: canAddGuests,
        }, cancellationConfig),
      },
    }))
  }, [canAddGuests, canBeCancelled, canChangeDates, canExtendDates, canRequestChangeDates, dispatch, freeCancellationPeriodEndDate, isCurrentOrder, item, offer, order, action, cancellationConfig])

  // Need to convince the types it's a hotel item
  const pkg = 'roomTypeName' in item.package ? item.package : undefined
  const reservationStartDate = item.reservation?.startDate
    ? dateFormatter(item.reservation.startDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR)
    : ''
  const reservationEndDate = item.reservation?.endDate
    ? dateFormatter(item.reservation.endDate, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR)
    : ''

  if (!item || !pkg) {
    return null
  }

  return <LesaPurchaseSummaryContainer>
    <LesaPurchaseSummaryHeader order={order}>
      <StructureLabel variant="standard">{roomLabel}</StructureLabel>
    </LesaPurchaseSummaryHeader>
    <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
      <Group direction="vertical" gap={4}>
        <BodyText variant="medium" weight="bold">{pkg.name}</BodyText>
        <BodyText variant="medium">{pkg.roomTypeName}</BodyText>
        {bookingNumber && <BodyText variant="small" colour="neutral-two"><strong>{intl.formatMessage({
          id: 'YjLVoUox1wvaPox9aFkk',
          defaultMessage: 'Booking ID:',
          description: 'Label for hotel item booking ID on the hotel or room booking page',
        })}
        </strong> {bookingNumber}
        </BodyText>}
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineUsersAltIcon colour="primary" size="XS" />
          <BodyText variant="medium" format="titlecase">
            {formatOccupantsShort(item.reservation)}
          </BodyText>
        </Group>
        {!!reservationStartDate && !!reservationEndDate && <Group direction="horizontal" gap={4} verticalAlign="center">
          <LineCalendarIcon size="XS" />
          <BodyText variant="medium">{reservationStartDate}</BodyText>
          <LineArrowRightIcon colour="neutral-four" size="XS" />
          <BodyText variant="medium">{reservationEndDate}</BodyText>
        </Group>}
      </Group>
      <Group gap={16} direction="vertical">
        <ImageWrapper>
          <ImageWithReservedSpace id={pkg.roomTypeImage ?? item?.offer.image.id} />
        </ImageWrapper>
        {!hideSelectButton && <TextButton onClick={onSelect} fit="mobile-full-width" size="small" kind="secondary">{intl.formatMessage({
          id: 'HxLoKDQxg3hlDticRjzn',
          defaultMessage: 'Select',
          description: 'Button to select a hotel room item on the hotel or room booking page',
        })}
        </TextButton>}
      </Group>
    </Group>
  </LesaPurchaseSummaryContainer>
}

export default LesaHotelItemSummary
